/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';
import { EditorialStepsSection } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection';
import { Section } from 'ui/molecules/Section/Section';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';
import { DataFlowSection } from 'ui/organisms/DataFlowSection/DataFlowSection';
import { PillarCards } from 'ui/organisms/PillarCards/PillarCards';
import { StickyRailSection } from 'ui/organisms/StickyRailSection/StickyRailSection';

import { CapabilityShowcase } from 'landing-pages/master-data-bulk-feed/sections/CapabilityShowcase/CapabilityShowcase';

import styles from './MasterDataManagementPage.module.scss';

import {
  getAnatomyRoutes,
  getCapabilityTabs,
  getDeliveryChannels,
  getFaqItems,
  getMastersSteps,
  getTrustPillars,
} from './MasterDataManagementPage.data';

const MasterDataManagementPage = () => {
  const { secondaryNavigation } = useContext(ContentContext);
  const { t } = useInt();

  const matchTestRoute = secondaryNavigation?.book_demo?.slug;

  const matchTestCta = (
    <Button type="blue" size="xxl" route={matchTestRoute || '/book-a-demo'}>
      {t('Run a match test')}
    </Button>
  );

  return (
    <div className={styles.wrapper}>
      <Section
        className={styles.hero}
        titleSize="page"
        title={t('Master every company record. Authoritatively.')}
        description={t(
          'Global Database resolves the company records in your CRM, ERP, and billing systems against 600M+ registry-verified profiles from 400+ government sources. One golden record per company. Synced daily, with the filing to prove it.',
        )}
        actions={matchTestCta}
      />

      <PillarCards theme="dark" items={getTrustPillars(t)} />

      <EditorialStepsSection
        extra={t('The golden record')}
        title={t('Every field traces back to a filing.')}
        description={t(
          'Most MDM programs fail at the reference data layer: the master record is only as trustworthy as the source behind it. Global Database anchors each master record to the legal identity a government registry holds — so every attribute carries a registry name, a filing link, and a retrieval timestamp.',
        )}
        steps={getAnatomyRoutes(t)}
      />

      <CtaSection
        spotlight
        theme="dark"
        title={t('Explore the data dictionary.')}
        description={t(
          'Every attribute in a golden record — legal identity, status, hierarchy, financials, officers — with the registry it comes from and the format it ships in.',
        )}
        actions={
          <Button type="blue" size="xl" href="/data-dictionary">
            {t('View data dictionary →')}
          </Button>
        }
      />

      <StickyRailSection
        eyebrow={t('Who runs on this')}
        title={t('Every function breaks its master data differently.')}
        description={t(
          'Customer, vendor, and legal entity masters fail in different ways, for different owners, with different consequences. The remedy is the same in all three: anchor every record to the legal identity a government registry holds.',
        )}
        blocks={getMastersSteps(t)}
      />

      <CapabilityShowcase
        title={t('Match. Enrich. Link. Maintain.')}
        description={t(
          'Five steps between the file you have and the master record you need. Each one reads the registry of record directly, and shows you the filing behind every value it writes back.',
        )}
        tabs={getCapabilityTabs(t)}
      />

      <DataFlowSection
        extra={t('How you get the data')}
        title={t('Five ways to get the data into your stack.')}
        sourceTitle={
          <>
            {t('One golden record.')}
            <br />
            {t('600M+ companies.')}
          </>
        }
        sourceText={t(
          'Sourced from 400+ government registries across 200+ countries, every field attributed to its filing — available through any of these:',
        )}
        channels={getDeliveryChannels(t)}
      />

      <CtaSection
        theme="dark"
        glow
        title={t('Send us your worst file. We will send back the match rate.')}
        description={t(
          'A no-commitment match test against your own CRM or ERP export: matched entities, duplicates found, fields we can fill, and the registries behind each one.',
        )}
        actions={
          <Button type="white" size="xl" route={matchTestRoute || '/book-a-demo'}>
            {t('Run a match test')}
          </Button>
        }
      />

      <Faq
        title={t('The answers buyers ask for first.')}
        description={t('Still unsure? Send a file and see the result on your own data.')}
        data={getFaqItems(t)}
      />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default MasterDataManagementPage;
